import type { AccountRole, AccountStatus, AdminAccount, CareerProgress, CloudDatasetSummary, NormalizedRunV2, OverallAnalyzeRequest, OverallAnalyzeResponse, OverallMeta } from '../domain/types';
import type { DatasetSyncState } from './repositories';
import { validateNormalizedRun } from '../domain/schemas';
import { cloudRunFingerprint } from '../domain/cloudIdentity';
import { ApiError, apiFetch } from '../lib/api';

type CloudData = { runs: NormalizedRunV2[]; progress: CareerProgress | null };
export type PersonalCloudData = CloudData & { dataset: CloudDatasetSummary | null };
export type CloudSyncMetadata = Pick<DatasetSyncState, 'runManifest' | 'localToCloudIds' | 'progressDigest'>;
export type CloudUploadResult = { dataset: CloudDatasetSummary; sync: Required<CloudSyncMetadata>; mode: 'full' | 'incremental' };

function parseRuns(value: unknown): NormalizedRunV2[] {
  return (Array.isArray(value) ? value : []).flatMap((row) => {
    try { return [validateNormalizedRun({ ...(row as Record<string, unknown>), raw: {} })]; }
    catch { return []; }
  });
}

async function pseudonymousRunId(run: NormalizedRunV2): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(cloudRunFingerprint(run)));
  return `run-${[...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('').slice(0, 32)}`;
}

async function digestText(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function preparedRun(run: NormalizedRunV2): Promise<{ localId: string; cloudId: string; digest: string; run: NormalizedRunV2 }> {
  const sanitized = await sanitizedRun(run);
  return { localId: run.id, cloudId: sanitized.id, digest: await digestText(JSON.stringify(sanitized)), run: sanitized };
}

async function progressDigest(progress: CareerProgress | null): Promise<string> {
  return digestText(JSON.stringify(progress));
}

async function fullUploadState(runs: NormalizedRunV2[], progress: CareerProgress | null): Promise<{ prepared: Awaited<ReturnType<typeof preparedRun>>[]; sync: Required<CloudSyncMetadata> }> {
  const prepared = await Promise.all(runs.filter((run) => run.sourceScope === 'history').map(preparedRun));
  const unique = [...new Map(prepared.map((row) => [row.cloudId, row])).values()];
  return {
    prepared: unique,
    sync: {
      runManifest: Object.fromEntries(unique.map((row) => [row.cloudId, row.digest])),
      localToCloudIds: Object.fromEntries(unique.map((row) => [row.localId, row.cloudId])),
      progressDigest: await progressDigest(progress)
    }
  };
}

async function sanitizedRun(run: NormalizedRunV2): Promise<NormalizedRunV2> {
  const id = await pseudonymousRunId(run);
  return { ...run, id, sourceScope: 'history', sourceKey: `cloud:${id}`, fileName: `${id}.run`, seed: '', raw: {} };
}

export function aggregateCareerProgress(progresses: CareerProgress[]): CareerProgress | null {
  if (!progresses.length) return null;
  const characters = new Map<string, Record<string, unknown> & { character: string; wins: number; losses: number }>();
  const discoveries = new Map<string, Set<string>>();
  for (const progress of progresses) {
    for (const row of progress.characterStats || []) {
      const character = String(row.character || 'Unknown');
      const current = characters.get(character) || { character, wins: 0, losses: 0, playtime: 0, maxAscension: 0, bestWinStreak: 0 };
      current.wins = Number(current.wins || 0) + Number(row.wins || 0);
      current.losses = Number(current.losses || 0) + Number(row.losses || 0);
      current.playtime = Number(current.playtime || 0) + Number(row.playtime || 0);
      current.maxAscension = Math.max(Number(current.maxAscension || 0), Number(row.maxAscension || 0));
      current.bestWinStreak = Math.max(Number(current.bestWinStreak || 0), Number(row.bestWinStreak || 0));
      characters.set(character, current);
    }
    for (const [kind, ids] of Object.entries(progress.discoveries || {})) {
      const values = discoveries.get(kind) || new Set<string>();
      for (const id of ids || []) values.add(id);
      discoveries.set(kind, values);
    }
  }
  return {
    characterStats: [...characters.values()], cardStats: [], encounterStats: [], enemyStats: [], ancientStats: [], epochs: [],
    discoveries: Object.fromEntries([...discoveries].map(([kind, ids]) => [kind, [...ids]])),
    totalPlaytime: progresses.reduce((sum, progress) => sum + Number(progress.totalPlaytime || 0), 0),
    floorsClimbed: progresses.reduce((sum, progress) => sum + Number(progress.floorsClimbed || 0), 0),
    totalUnlocks: progresses.reduce((sum, progress) => sum + Number(progress.totalUnlocks || 0), 0)
  };
}

export const cloudRepository = {
  async getMyLatestDataset(): Promise<CloudDatasetSummary | null> {
    return (await apiFetch<{ dataset: CloudDatasetSummary | null }>('/api/datasets/latest')).dataset;
  },
  async getMyLatestData(): Promise<PersonalCloudData> {
    const data = await apiFetch<{ dataset: CloudDatasetSummary | null; runs: unknown; progress: CareerProgress | null }>('/api/datasets/latest/data');
    return { dataset: data.dataset, runs: parseRuns(data.runs), progress: data.progress };
  },
  async uploadLatestData(runs: NormalizedRunV2[], progress: CareerProgress | null): Promise<CloudDatasetSummary> {
    return (await this.uploadLatestDataWithState(runs, progress)).dataset;
  },
  async uploadLatestDataWithState(runs: NormalizedRunV2[], progress: CareerProgress | null): Promise<CloudUploadResult> {
    const state = await fullUploadState(runs, progress);
    const dataset = (await apiFetch<{ dataset: CloudDatasetSummary }>('/api/datasets/upload', { method: 'POST', body: JSON.stringify({ runs: state.prepared.map((row) => row.run), progress }) })).dataset;
    return { dataset, sync: state.sync, mode: 'full' };
  },
  async syncLatestData(runs: NormalizedRunV2[], progress: CareerProgress | null, state: DatasetSyncState | null, delta?: { upsertRunIds: string[]; removedRunIds: string[]; progressChanged: boolean }): Promise<CloudUploadResult> {
    if (!state?.datasetId || typeof state.revision !== 'number' || !state.runManifest || !state.localToCloudIds || !delta) return this.uploadLatestDataWithState(runs, progress);
    const byId = new Map(runs.map((run) => [run.id, run]));
    const changedRuns = delta.upsertRunIds.flatMap((id) => byId.get(id) ? [byId.get(id)!] : []);
    if (changedRuns.length !== delta.upsertRunIds.length || delta.removedRunIds.some((id) => !state.localToCloudIds?.[id])) return this.uploadLatestDataWithState(runs, progress);
    const upserts = await Promise.all(changedRuns.map(preparedRun));
    const removedCloudIds = [...new Set([
      ...delta.removedRunIds.map((id) => state.localToCloudIds![id]),
      ...upserts.flatMap((row) => state.localToCloudIds![row.localId] && state.localToCloudIds![row.localId] !== row.cloudId ? [state.localToCloudIds![row.localId]] : [])
    ])];
    try {
      const dataset = (await apiFetch<{ dataset: CloudDatasetSummary }>('/api/datasets/sync', {
        method: 'POST',
        body: JSON.stringify({ baseDatasetId: state.datasetId, baseRevision: state.revision, upserts: upserts.map((row) => row.run), removedRunIds: removedCloudIds, progressChanged: delta.progressChanged, progress: delta.progressChanged ? progress : undefined, parserVersion: runs[0]?.parserVersion || 'unknown' })
      })).dataset;
      const runManifest = { ...state.runManifest };
      const localToCloudIds = { ...state.localToCloudIds };
      for (const localId of delta.removedRunIds) { const cloudId = localToCloudIds[localId]; delete localToCloudIds[localId]; if (cloudId) delete runManifest[cloudId]; }
      for (const row of upserts) { const previousCloudId = localToCloudIds[row.localId]; if (previousCloudId && previousCloudId !== row.cloudId) delete runManifest[previousCloudId]; localToCloudIds[row.localId] = row.cloudId; runManifest[row.cloudId] = row.digest; }
      return { dataset, sync: { runManifest, localToCloudIds, progressDigest: delta.progressChanged ? await progressDigest(progress) : state.progressDigest || await progressDigest(progress) }, mode: 'incremental' };
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) throw new ApiError('云端存档已在其他设备更新；已保留本地变更，请先刷新云端基线再重试。', 409);
      throw error;
    }
  },
  async getOverallMeta(): Promise<OverallMeta> {
    return (await apiFetch<{ meta: OverallMeta }>('/api/overall/meta')).meta;
  },
  async analyzeOverall(request: OverallAnalyzeRequest): Promise<OverallAnalyzeResponse> {
    return apiFetch<OverallAnalyzeResponse>('/api/overall/analyze', {
      method: 'POST',
      body: JSON.stringify(request),
      timeoutMs: 75_000,
      maxAttempts: 2,
      retryStatuses: [429, 500, 502, 503, 504]
    });
  },
  async listAdminAccounts(): Promise<AdminAccount[]> {
    return (await apiFetch<{ accounts: AdminAccount[] }>('/api/admin/accounts')).accounts;
  },
  async getAdminAccountData(accountId: string): Promise<CloudData> {
    const data = await apiFetch<{ runs: unknown; progress: CareerProgress | null }>(`/api/admin/accounts/${encodeURIComponent(accountId)}/data`);
    return { runs: parseRuns(data.runs), progress: data.progress };
  },
  async updateAdminAccount(accountId: string, patch: { role?: AccountRole; status?: AccountStatus }): Promise<void> {
    await apiFetch(`/api/admin/accounts/${encodeURIComponent(accountId)}`, { method: 'PATCH', body: JSON.stringify(patch) });
  }
};
